import { useEffect, useRef } from "react";
import type { HandFrame, HandsData } from "../api/types";

// MediaPipe / MANO bone connections (21 keypoints).
const HAND_EDGES: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [0, 9], [9, 10], [10, 11], [11, 12],
  [0, 13], [13, 14], [14, 15], [15, 16],
  [0, 17], [17, 18], [18, 19], [19, 20],
];

const COLOR_LEFT = "#22d3ee"; // cyan
const COLOR_RIGHT = "#f472b6"; // magenta
const JOINT_FILL = "rgba(255,255,255,0.85)";
const MATCH_WINDOW = 0.08; // seconds

/** Binary search for the frame whose |t - target| is smallest. Returns null if none ≤ window. */
function nearestFrame(frames: HandFrame[], t: number): HandFrame | null {
  if (frames.length === 0) return null;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  // lo is first frame with t >= target; compare it and its predecessor.
  let best = frames[lo];
  if (lo > 0 && Math.abs(frames[lo - 1].t - t) < Math.abs(best.t - t)) {
    best = frames[lo - 1];
  }
  return Math.abs(best.t - t) <= MATCH_WINDOW ? best : null;
}

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  hands: HandsData;
  enabled: boolean;
}

/**
 * A <canvas> absolutely positioned over the <video>. It tracks the rendered box of the
 * video via ResizeObserver (DPR-aware backing store) and draws the nearest hand frame
 * on a requestAnimationFrame loop while playing, plus on seek/resize.
 */
export default function HandOverlay({ videoRef, hands, enabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const framesRef = useRef<HandFrame[]>([]);
  const rafRef = useRef<number>(0);

  // Time-sorted frames (defensive copy/sort once per dataset).
  useEffect(() => {
    framesRef.current = [...hands.frames].sort((a, b) => a.t - b.t);
  }, [hands]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let cssW = 0;
    let cssH = 0;

    const syncSize = () => {
      const rect = video.getBoundingClientRect();
      cssW = rect.width;
      cssH = rect.height;
      const dpr = window.devicePixelRatio || 1;
      const bw = Math.max(1, Math.round(cssW * dpr));
      const bh = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const clear = () => ctx.clearRect(0, 0, cssW, cssH);

    const draw = () => {
      if (!enabled || cssW === 0 || cssH === 0) {
        clear();
        return;
      }
      const frame = nearestFrame(framesRef.current, video.currentTime);
      clear();
      if (!frame) return;

      const sx = cssW / hands.image_width;
      const sy = cssH / hands.image_height;

      for (const hand of frame.hands) {
        const color = hand.side === "left" ? COLOR_LEFT : COLOR_RIGHT;
        const kp = hand.kp;
        // bones
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        for (const [a, b] of HAND_EDGES) {
          const pa = kp[a];
          const pb = kp[b];
          if (!pa || !pb) continue;
          ctx.moveTo(pa[0] * sx, pa[1] * sy);
          ctx.lineTo(pb[0] * sx, pb[1] * sy);
        }
        ctx.stroke();
        // joints
        ctx.fillStyle = JOINT_FILL;
        for (const p of kp) {
          if (!p) continue;
          ctx.beginPath();
          ctx.arc(p[0] * sx, p[1] * sy, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const loop = () => {
      draw();
      rafRef.current = requestAnimationFrame(loop);
    };

    // ResizeObserver keeps the backing store matched to the rendered video box.
    const ro = new ResizeObserver(() => {
      syncSize();
      draw();
    });
    ro.observe(video);

    // One-shot redraws on discrete events so we update even while paused.
    const onSeek = () => draw();
    const onLoaded = () => {
      syncSize();
      draw();
    };
    video.addEventListener("seeked", onSeek);
    video.addEventListener("seeking", onSeek);
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("loadeddata", onLoaded);

    syncSize();
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      video.removeEventListener("seeked", onSeek);
      video.removeEventListener("seeking", onSeek);
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("loadeddata", onLoaded);
    };
  }, [videoRef, hands, enabled]);

  return <canvas ref={canvasRef} className="hand-overlay-canvas" aria-hidden />;
}
